import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';

// components
import OverlayLoader from '../../../parts/OverlayLoader';

// actions
import { updateUser } from '../../../actions/user';

// services
import { validatePostCode } from '../../../services/UserService';


const USER_TEMPLATE = {
  title: 'Mr',
  firstName: '',
  surname: '',
  addresses: [],
  contactOptions: [],
  emailAddresses: [],
  phoneNumbers: []
};

const ADDRESS_TEMPLATE = {
  houseNameNumber: '',
  line1: '',
  line2: '',
  line3: '',
  line4: '',
  postCode: '',
  type: ''
};

const EMAIL_TEMPLATE = {
  address: '',
  primary: false
};

const PHONE_TEMPLATE = {
  number: '',
  cantText: false,
  primary: false,
};

class AdminDetailsEditor extends Component {
  constructor(props) { // take full user object!!
    super(props);
    this.createUserState = this.createUserState.bind(this);
    this.handleUserUpdate = this.handleUserUpdate.bind(this);
    this.validatePhoneNumbers = this.validatePhoneNumbers.bind(this);
  }

  createUserState(user) {
    return {
      ...USER_TEMPLATE,
      ...user,
      title: user.title || USER_TEMPLATE.title,
      addresses: [...user.addresses || []],
      emailAddresses: [...user.emailAddresses || []],
      phoneNumbers: [...user.phoneNumbers || []]
    };
  }

  validPrimaryState(arr) {
    return arr.reduce((acc, record) => record.primary ? acc + 1 : acc, 0) <= 1;
  }

  validatePhoneNumbers(phoneNumbers) {
    const containsProhibited = phoneNumber => new RegExp(/[^0-9+\s]/, 'g').test(phoneNumber);
    let errors = phoneNumbers.reduce((acc, record) => ([
      ...acc,
      ...(containsProhibited(record.number) ? [`${record.number} is not a valid phone number`] : [])
    ]), []);
    errors = [
      ...errors,
      ...(phoneNumbers.length > 0 && phoneNumbers.find(num => num.primary) ? [] : ['a customer must have a primary phone number']),
      ...(this.validPrimaryState(phoneNumbers) ? [] : ['a customer can only have one primary phone number'])
    ];
    return errors;
  }

  validateEmailAddresses(emailAddresses) {
    return this.validPrimaryState(emailAddresses) ? [] : ['a customer can only have one primary email address'];
  }

  validateAddresses(addresses) {
    return this.validPrimaryState(addresses) ? [] : ['a customer can only have one primary address'];
  }

  validateUser(user) {
    return [
      ...this.validatePhoneNumbers(user.phoneNumbers),
      // ...this.validateEmailAddresses(user.emailAddresses), // not yet supported in all systems
      ...this.validateAddresses(user.addresses)
    ];
  }

  handleHouseNameNumberWorkaround(address) {
    const hasHouseNameNumber = address.houseNameNumber && address.houseNameNumber.trim().length > 0;
    const hasLine1 = address.line1 && address.line1.trim().length > 0;
    const houseNameNumberInLine1 = address.line1.includes(address.houseNameNumber.trim());
    if(hasHouseNameNumber && hasLine1 && !houseNameNumberInLine1) {
      return { ...address, line1: `${address.houseNameNumber} ${address.line1}` };
    }
    return address;
  }

  async handleUserUpdate(newUserData) {
    const validationErrors = this.validateUser(newUserData);
    if (validationErrors.length) {
      return { [FORM_ERROR]: validationErrors };
    }
    // workaround data manipulation
    const alteredAddresses = newUserData.addresses.map(this.handleHouseNameNumberWorkaround);
    // port over postcodeValidation => make all async when possible
    if(newUserData.addresses) {
      // force validate to always resolved so Promise.all can collect all the errors
      const validatePostCodes = newUserData.addresses.map(
        address => validatePostCode((address.postCode || '').trim())
                    .catch(e => Promise.resolve(e)).then(e => e));

      const validation = await Promise.all(validatePostCodes);
      const fails = validation.filter(a => a !== true);
      if(fails.length) return { [FORM_ERROR]: fails };
    }

    return this.props.updateUser({ ...newUserData, addresses: alteredAddresses })
      .then(() => this.props.onCancel())
      .catch(() => Promise.resolve({ [FORM_ERROR]: ['Failed to update user.'] }));
  }

  renderEmails(controls) {
    return (
      <fieldset>
        <legend>Email Addresses</legend>
        <FieldArray name="emailAddresses">
          {({ fields }) =>
            fields.map((email, index) => (
              <div key={email}>

                <label htmlFor={`${email}.address`}>email</label>
                <Field className="type__input" name={`${email}.address`} component="input" type="email" placeholder="sofayoulove@home.com" required />

                <section className="u-flex u-flex-justify-space-between u-w-100 u-padding-top-tiny u-padding-bottom-tiny">
                  <label htmlFor={`${email}.primary`}>primary</label>
                  <Field name={`${email}.primary`} component="input" type="checkbox" />

                  {!fields.value[index].id && <button className="c-btn c-btn--link" type="button" onClick={() => fields.remove(index)} >remove</button>}
                </section>
              </div>
            )
            )}
        </FieldArray>
        <button className="c-btn  c-btn--wbg" type="button" onClick={() => controls.push('emailAddresses', EMAIL_TEMPLATE)} >
          Add email address
        </button>
      </fieldset>
    );
  }

  renderPhoneNumbers(controls) {
    return (
      <fieldset>
        <legend>Phone Numbers</legend>
        <FieldArray name="phoneNumbers">
          {({ fields }) =>
            fields.map((phoneNumber, index) => (
              <div key={phoneNumber}>

                <label htmlFor={`${phoneNumber}.number`}>number</label>
                <Field className="type__input" name={`${phoneNumber}.number`} component="input" type="text" placeholder="09283409823" required />

                <section className="u-flex u-flex-justify-space-between u-w-100 u-padding-top-tiny u-padding-bottom-tiny phNumbers">
                  <label htmlFor={`${phoneNumber}.primary`}>primary</label>
                  <Field name={`${phoneNumber}.primary`} component="input" type="checkbox" />

                  <label htmlFor={`${phoneNumber}.canText`}>can text</label>
                  <Field name={`${phoneNumber}.canText`} component="input" type="checkbox" />

                  {!fields.value[index].id && <button className="c-btn c-btn--link" type="button" onClick={() => fields.remove(index)} >remove</button>}
                </section>
              </div>
            )
            )}
        </FieldArray>
        <button className="c-btn  c-btn--wbg" type="button" onClick={() => controls.push('phoneNumbers', PHONE_TEMPLATE)} >
          Add Phone number
        </button>
      </fieldset>
    );
  }

  renderAddresses(controls) {
    return (
      <fieldset>
        <legend>Addresses</legend>
        <FieldArray name="addresses">
          {({ fields }) => (
            <React.Fragment>

              {fields.map((address, index) => (
                <div key={address}>


                <label htmlFor={`${address}.type`}>Address Type</label>
                <Field className="type__select" name={`${address}.type`} component="select">
                  <option value="Delivery">{fields.value[index].type}</option>
                </Field> 

                <label htmlFor={`${address}.houseNameNumber`}>House name or number</label>
                <Field className="type__input" name={`${address}.houseNameNumber`} component="input" type="text" required />

                <label htmlFor={`${address}.line1`}>line1</label>
                <Field className="type__input" name={`${address}.line1`} component="input" type="text" required />

                <label htmlFor={`${address}.line2`}>line2</label>
                <Field className="type__input" name={`${address}.line2`} component="input" type="text" required />

                <label htmlFor={`${address}.line3`}>line3</label>
                <Field className="type__input" name={`${address}.line3`} component="input" type="text" />

                <label htmlFor={`${address}.line4`}>line4</label>
                <Field className="type__input" name={`${address}.line4`} component="input" type="text" />

                <label htmlFor={`${address}.postCode`}>postCode</label>
                <Field className="type__input" name={`${address}.postCode`} component="input" type="text" required />

                <section className="u-flex u-flex-justify-space-between u-w-100 u-padding-top-tiny u-padding-bottom-tiny">

                    { /* !fields.value[index].id &&
                      <button className="c-btn c-btn--link" type="button" onClick={() => fields.remove(index)}>
                        remove
                    </button> */}

                  </section>
                </div>
              ))}

              { fields.length < 2 && (
                <button
                  className="c-btn  c-btn--wbg"
                  type="button"
                  onClick={() => { controls.push('addresses', { ...ADDRESS_TEMPLATE, type: (fields.length < 1 ? 'delivery' : 'billing') }) } 
                  }
                >
                  Add address
                </button>
              )}

            </React.Fragment>
          )}
        </FieldArray>

      </fieldset>
    );
  }

  render() {
    return (
      <div className="delivery__details u-padding-small u-margin-y-small u-pale-grey-bg u-position-relative">

        <Form
          onSubmit={data => this.handleUserUpdate(data)}
          mutators={{
            ...arrayMutators
          }}
          initialValues={this.createUserState(this.props.user)}
          render={({
            handleSubmit,
            submitError,
            submitting,
            form: { mutators: { push, remove } } // injected from final-form-arrays above (mutators
          }) => (
              <form method="POST" onSubmit={handleSubmit}>

                <OverlayLoader active={submitting} scale={0.5} background="rgba(255, 255, 255, 0.8)" />

                <fieldset>

                  <label htmlFor="title">Title</label>
                  <Field className="type__select" name="title" component="select" >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Rev">Rev</option>
                  </Field>

                  <label htmlFor="firstName">First name</label>
                  <Field className="type__input" name="firstName" component="input" />

                  <label htmlFor="surname">Surname</label>
                  <Field className="type__input" name="surname" component="input" />

                </fieldset>

                {/* {this.renderEmails({ push, remove })} */}
                {this.renderAddresses({ push, remove })}
                {this.renderPhoneNumbers({ push, remove })}

                <div className="c-event-feed__card__splitter u-padding-x-small u-flex-justify-end">
                  <button className="u-pull-right c-btn c-btn--text clickable-link" type="submit">save</button>
                  <button onClick={this.props.onCancel} className="u-pull-right c-btn c-btn--text clickable-link">close</button>
                </div>
                {submitError && (
                  <div className="errors">
                    {submitError.map((e, i) => <div key={`ud-error${i}`}>{e}</div>)}
                  </div>
                )}

              </form>
            )}
        />

      </div>
    );
  }
}

const mapDispatchToProps = dispatch => bindActionCreators({
  updateUser
}, dispatch);

export default connect(null, mapDispatchToProps)(AdminDetailsEditor);



// WEBPACK FOOTER //
// ./src/cards/templates/UserDetailsCard/AdminDetailsEditor.js